import type { BrowserTranslationStatus } from "../../shared/game";

const normalize = (text: string) => text.replace(/\s+/gu, " ").trim();

/** Browser translation has no standard API: compare server-owned quiz text as well as page markers. */
export function readPHTranslation(root: HTMLElement, expected: readonly string[]): BrowserTranslationStatus | undefined {
    const texts = [...root.querySelectorAll<HTMLElement>(".ph-question-copy h1, .ph-answer-text")];
    // Media/partial mounting is unknown, not proof that translation was disabled.
    if (!expected.length || texts.length !== expected.length) return undefined;
    const changed = texts.some((el, index) => normalize(el.textContent ?? "") !== normalize(expected[index]));
    return changed ? "content" : document.documentElement.matches(".translated-ltr, .translated-rtl") ? "page" : null;
}

export class PHTranslationMonitor {
    private observer: MutationObserver;
    private timer = 0;
    private retryTimer: number;
    private last: BrowserTranslationStatus | undefined;
    private observed: BrowserTranslationStatus | undefined;
    private expected: readonly string[] = [];
    private active = false;
    private force = false;
    constructor(private root: HTMLElement, private report: (status: BrowserTranslationStatus) => boolean) {
        this.observer = new MutationObserver(records => {
            if (records.some(record => record.target === document.documentElement ||
                (record.target instanceof Element ? record.target : record.target.parentElement)?.closest(".ph-question-copy, .ph-answer-text"))) this.schedule();
        });
        this.observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
        this.observer.observe(root, { childList: true, characterData: true, subtree: true });
        this.retryTimer = window.setInterval(() => this.schedule(), 2000);
    }
    update(expected: readonly string[], active: boolean) {
        this.expected = expected;
        this.active = active;
        this.schedule();
    }
    reconnect() { this.force = true; this.schedule(); }
    private schedule() {
        // Throttle instead of debounce: regular room redraws must not starve detection.
        if (!this.timer) this.timer = window.setTimeout(() => {
            this.timer = 0;
            if (!this.active) return;
            const detected = readPHTranslation(this.root, this.expected);
            if (detected !== undefined) this.observed = detected;
            // Preserve unsent evidence too if a media screen replaces the quiz.
            const status = this.observed;
            if (status === undefined) return;
            if (this.force || status !== this.last) {
                if (this.report(status)) {
                    this.last = status;
                    this.force = false;
                    try { if (localStorage.getItem("ziarcadia:translation-debug") !== "0") console.info(`[Ziarcadia][Translation] status=${status ?? "original"}`); } catch {}
                }
            }
        }, 250);
    }
    destroy() { this.observer.disconnect(); clearTimeout(this.timer); clearInterval(this.retryTimer); }
}
